'use client';

import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Users,
  Building2,
  ExternalLink,
  ShieldAlert,
  AlertTriangle,
  Search,
  ArrowUpDown,
  Pencil,
} from 'lucide-react';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@tagadapay/trpc-types';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ADMIN_ROUTES, PARTNER_ADMIN_ROUTES } from '@/config/router';
import { useSuperAdmin } from '@/hooks/use-superadmin';
import { trpc } from '@/lib/api/trpc';
import { cn } from '@/lib/utils';

type PartnersListOutput = inferRouterOutputs<AppRouter>['partners']['listWithOrgs'];
type PartnerRow = PartnersListOutput['partners'][number];

type SortKey = 'slug' | 'mode' | 'orgs' | 'staff' | 'deal';
type SortDir = 'asc' | 'desc';

/**
 * Superadmin Partners macro view.
 *
 * Read-only window onto the `partners` DB table (loaded by
 * `partners.listWithOrgs` → `loadAllPartners()` in
 * `src/lib/partners/deals.ts`). Partner CRUD lives in the Hub at
 * `hub.tagada.io/partners` — this page is just a dense triage table:
 * mode, deal, orgs, staff, host. The code registry
 * `apps/crm-app/src/config/partners.ts` is server / edge only (the Vercel
 * middleware injects the current host's partner), so this page never loads it.
 *
 * Click a row → drills into the per-partner admin dashboard.
 *
 * Scope reminder: PARTNER MODE only. Whitelabel forks (apptics, fmj as
 * a full deploy) are separate Vercel projects and don't show up here.
 */
export default function AdminPartnersPage() {
  const navigate = useNavigate();
  const { isSuperAdmin, isLoading: loadingRole } = useSuperAdmin();

  // No client-side registry: `apps/crm-app/src/config/partners.ts` is server /
  // edge only (it would ship every partner to every partner host). The drift
  // indicator against it therefore stays empty here until the backend reads
  // that file itself.
  const { data, isLoading, error } = trpc.partners.listWithOrgs.useQuery(undefined, {
    enabled: isSuperAdmin,
    staleTime: 60_000,
  });

  const diagnose = trpc.partners.diagnoseSessionClaims.useQuery(undefined, {
    enabled: isSuperAdmin,
    staleTime: 5 * 60 * 1000,
  });

  const [search, setSearch] = React.useState('');
  const [sort, setSort] = React.useState<{ key: SortKey; dir: SortDir }>({
    key: 'orgs',
    dir: 'desc',
  });

  const partners = data?.partners ?? [];
  const untracked = data?.untracked ?? [];

  const visible = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = partners;
    if (q) {
      rows = rows.filter(
        (p) =>
          p.slug.toLowerCase().includes(q) ||
          p.label.toLowerCase().includes(q) ||
          (p.domain?.appHost ?? '').toLowerCase().includes(q),
      );
    }
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      switch (sort.key) {
        case 'slug':
          return a.slug.localeCompare(b.slug) * dir;
        case 'mode':
          return a.mode.localeCompare(b.mode) * dir;
        case 'orgs':
          return (a.orgCount - b.orgCount) * dir;
        case 'staff':
          return (a.staffCount - b.staffCount) * dir;
        case 'deal': {
          // Sort by CRM partner share %; "no deal" rows sink last.
          const av = a.deal?.crm.partnerSharePct ?? -1;
          const bv = b.deal?.crm.partnerSharePct ?? -1;
          return (av - bv) * dir;
        }
        default:
          return 0;
      }
    });
  }, [partners, search, sort]);

  const toggleSort = React.useCallback((key: SortKey) => {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
  }, []);

  if (loadingRole) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  if (!isSuperAdmin) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="mx-auto h-12 w-12 text-muted-foreground" />
          <h2 className="mt-4 text-xl font-semibold">Access Denied</h2>
          <p className="mt-2 max-w-md text-sm text-muted-foreground">
            The Partners panel is restricted to superadmin users.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Partners</h1>
          <Badge variant="secondary" className="text-xs">superadmin</Badge>
        </div>
        <Button onClick={() => navigate(ADMIN_ROUTES.PARTNERS_NEW)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Partner
        </Button>
      </div>

      {diagnose.data && !diagnose.data.tgdRolesPresent ? (
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Session claim misconfiguration</AlertTitle>
          <AlertDescription>{diagnose.data.hint}</AlertDescription>
        </Alert>
      ) : null}

      {untracked.length > 0 ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Untracked whitelabel tags</AlertTitle>
          <AlertDescription>
            <p className="mb-2">
              These whitelabel tags are set on Clerk orgs but don&apos;t match any partner row in
              the DB. Likely typos or leftover data from a renamed partner.
            </p>
            <ul className="ml-4 list-disc space-y-0.5">
              {untracked.map((u) => (
                <li key={u.tag} className="font-mono text-xs">
                  {u.tag} — {u.orgCount} org{u.orgCount === 1 ? '' : 's'}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Failed to load partners</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      ) : null}

      <div className="flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search slug, label, or host…"
            className="pl-8"
          />
        </div>
        <div className="text-xs tabular-nums text-muted-foreground">
          {visible.length}
          {visible.length !== partners.length ? ` / ${partners.length}` : ''} partner
          {partners.length === 1 ? '' : 's'}
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHead label="Slug" k="slug" sort={sort} onToggle={toggleSort} className="w-[220px]" />
              <SortableHead label="Mode" k="mode" sort={sort} onToggle={toggleSort} className="w-[140px]" />
              <SortableHead label="Orgs" k="orgs" sort={sort} onToggle={toggleSort} className="w-[80px] text-right" />
              <SortableHead label="Staff" k="staff" sort={sort} onToggle={toggleSort} className="w-[80px] text-right" />
              <SortableHead label="Deal (CRM / Spread)" k="deal" sort={sort} onToggle={toggleSort} className="w-[170px]" />
              <TableHead>Host</TableHead>
              <TableHead className="w-[60px] text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                  Loading partners…
                </TableCell>
              </TableRow>
            ) : visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                  {partners.length === 0
                    ? 'No partners yet. Add one from the hub.tagada.io admin.'
                    : 'No partners match this search.'}
                </TableCell>
              </TableRow>
            ) : (
              visible.map((p) => <PartnerTableRow key={p.slug} partner={p} />)
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// -------- table sub-components --------

function SortableHead({
  label,
  k,
  sort,
  onToggle,
  className,
}: {
  label: string;
  k: SortKey;
  sort: { key: SortKey; dir: SortDir };
  onToggle: (k: SortKey) => void;
  className?: string;
}) {
  const active = sort.key === k;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onToggle(k)}
        className={cn(
          'inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wider hover:text-foreground',
          active ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {label}
        <ArrowUpDown className={cn('h-3 w-3', active ? 'opacity-100' : 'opacity-30')} />
      </button>
    </TableHead>
  );
}

function PartnerTableRow({ partner: p }: { partner: PartnerRow }) {
  const navigate = useNavigate();
  const dest = PARTNER_ADMIN_ROUTES(p.slug).BASE;

  // Tooltip text rendered into the native title attribute so we don't
  // need a Tooltip provider here — the deal cell is dense enough that
  // the buy/merchant breakdown is more useful as on-hover detail.
  const dealTooltip = p.deal
    ? [
        `Buy rate: ${(p.deal.processing.buyRateBps / 100).toFixed(2)}% + $${(p.deal.processing.buyRateFixedCents / 100).toFixed(2)}`,
        `Merchant: ${(p.deal.processing.merchantRateBps / 100).toFixed(2)}% + $${(p.deal.processing.merchantFixedCents / 100).toFixed(2)}`,
        `Spread: ${(p.deal.processing.spreadRateBps / 100).toFixed(2)}% + $${(p.deal.processing.spreadFixedCents / 100).toFixed(2)}`,
      ].join('\n')
    : undefined;

  return (
    <TableRow
      onClick={() => navigate(dest)}
      className="cursor-pointer hover:bg-muted/40"
    >
      <TableCell className="font-mono">
        <div className="flex items-center gap-2">
          <span className="font-medium">{p.slug}</span>
          {/* `frontendMirrorMissing` is not shown: this page no longer sends the
              code registry (server / edge only), so the flag is always set. */}
        </div>
        <div className="truncate font-sans text-xs text-muted-foreground">{p.label}</div>
      </TableCell>
      <TableCell>
        <Badge
          variant={p.mode === 'partner-domain' ? 'default' : 'secondary'}
          className="text-[10px] uppercase"
        >
          {p.mode}
        </Badge>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        <span className="inline-flex items-center justify-end gap-1">
          <Building2 className="h-3 w-3 text-muted-foreground" />
          {p.orgCount}
        </span>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        <span className="inline-flex items-center justify-end gap-1">
          <Users className="h-3 w-3 text-muted-foreground" />
          {p.staffCount}
        </span>
      </TableCell>
      <TableCell title={dealTooltip}>
        {p.deal ? (
          <span className="font-mono text-xs">
            CRM {p.deal.crm.partnerSharePct}% · Spread {p.deal.processing.partnerSharePct}%
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        {p.domain ? (
          <a
            href={`https://${p.domain.appHost}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-mono text-xs hover:underline"
          >
            {p.domain.appHost}
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()} className="text-right">
        <div className="inline-flex items-center gap-1">
          <Button
            asChild
            variant="ghost"
            size="sm"
            title="Edit partner row (status, pricing group, domain…)"
          >
            <Link to={ADMIN_ROUTES.PARTNERS_EDIT(p.slug)}>
              <Pencil className="h-3.5 w-3.5" />
            </Link>
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link to={dest}>View</Link>
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
